import { useState, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import type { Service } from './ServicesPage';
import { resolveLabTestSuggestion, type AppointmentCategoryRow } from '@helios/data-access';

// ─── Styles (matching existing drawer patterns) ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text)',
  display: 'block',
  marginBottom: 'var(--sp-1)',
};

const inputBaseStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

const textareaStyle: CSSProperties = {
  ...inputBaseStyle,
  height: 80,
  padding: 'var(--sp-2) var(--input-padding-x)',
  resize: 'vertical',
};

// ─── Toggle ───
//
// Shared by the Active and Laboratory Test rows so the two controls are
// visually identical — PRD.305 asks for the laboratory control to sit in the
// existing Manage Services UI, not in a laboratory-specific editor.

function SwitchToggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Coming Soon Row ───

function ComingSoonRow({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--sp-2) 0',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          fontStyle: 'italic',
        }}
      >
        Coming soon
      </span>
    </div>
  );
}

// ─── Default form state ───

interface FormState {
  name: string;
  categoryId: string;
  description: string;
  duration: string;
  price: string;
  active: boolean;
  /** PRD.305 — canonical laboratory-test classification. */
  isLabTest: boolean;
}

function defaultForm(service?: Service | null, newIsLabTest = false): FormState {
  if (service) {
    // appointment_services.price_cents is stored in minor units (cents)
    // per the schema contract. The form takes major-unit input from the
    // user, so convert at the boundary on load and again on save.
    return {
      name: service.name,
      categoryId: service.categoryId ?? '',
      description: service.description,
      duration: String(service.duration),
      price: service.priceCents != null ? String(service.priceCents / 100) : '',
      active: service.active,
      isLabTest: service.isLabTest,
    };
  }
  return {
    name: '',
    categoryId: '',
    description: '',
    duration: '',
    price: '',
    active: true,
    // New services are not laboratory tests until the catalog says so — either
    // by the category convenience default below, by the user, or by opening
    // the drawer from the Laboratory Services view, which means it.
    isLabTest: newIsLabTest,
  };
}

// ─── Service Drawer ───

interface ServiceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  service: Service | null;
  categories: AppointmentCategoryRow[];
  onSave: (data: {
    name: string;
    categoryId: string | null;
    description: string;
    duration: number;
    priceCents: number | null;
    active: boolean;
    isLabTest: boolean;
  }) => void;
  /**
   * PRD.305 — what the Laboratory Test toggle starts at for a NEW service.
   * The Laboratory Services view passes true: a row created there that was not
   * a lab test would vanish from the list the moment it was saved. Editing an
   * existing service always uses that service's stored value instead.
   */
  defaultIsLabTest?: boolean;
}

export function ServiceDrawer({
  open,
  onOpenChange,
  brandTokens,
  service,
  categories,
  onSave,
  defaultIsLabTest = false,
}: ServiceDrawerProps) {
  const [form, setForm] = useState<FormState>(defaultForm(service, defaultIsLabTest));
  const isEdit = !!service;

  /**
   * PRD.305 — whether the user has explicitly set the Laboratory Test toggle in
   * this drawer session. A ref, not state: it must not re-trigger the
   * suggestion effect, and it only ever gates whether the category is allowed
   * to speak. Reset every time the drawer opens.
   */
  const labTestTouchedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(defaultForm(service, defaultIsLabTest));
      labTestTouchedRef.current = false;
    }
  }, [open, service]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const activeCategories = useMemo(
    () =>
      categories
        .filter((c) => c.active === 1)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [categories],
  );

  const selectedCategoryName = useMemo(
    () => categories.find((c) => c.id === form.categoryId)?.name ?? null,
    [categories, form.categoryId],
  );

  /**
   * PRD.305 — convenience only: picking the seeded "Lab Work" category on a NEW
   * service pre-enables Laboratory Test. `resolveLabTestSuggestion` (pure,
   * unit-tested) enforces the two rules that matter — editing an existing
   * service never auto-changes its saved value, and once the user has touched
   * the toggle the category stops overriding them.
   */
  useEffect(() => {
    if (!open) return;
    setForm((prev) => {
      const next = resolveLabTestSuggestion({
        mode: isEdit ? 'edit' : 'create',
        categoryName: selectedCategoryName,
        userTouched: labTestTouchedRef.current,
        current: prev.isLabTest,
      });
      return next === prev.isLabTest ? prev : { ...prev, isLabTest: next };
    });
  }, [open, isEdit, selectedCategoryName]);

  const canSave = form.name.trim() && form.duration.trim();

  const handleSave = () => {
    const priceVal = form.price.trim();
    let priceCents: number | null = null;
    if (priceVal !== '') {
      const major = parseFloat(priceVal);
      // Use string-based exact conversion to avoid IEEE-754 quirks like
      // 0.005 * 100 = 0.4999... (mirrors phpFromMajor's path in @helios/domain).
      if (Number.isFinite(major)) {
        const fixed = Math.abs(major).toFixed(2);
        const [whole, frac = '0'] = fixed.split('.');
        const sign = major < 0 ? -1 : 1;
        priceCents = sign * (parseInt(whole, 10) * 100 + parseInt(frac.padEnd(2, '0'), 10));
      }
    }
    onSave({
      name: form.name,
      categoryId: form.categoryId || null,
      description: form.description,
      duration: parseInt(form.duration, 10) || 0,
      priceCents,
      active: form.active,
      isLabTest: form.isLabTest,
    });
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit Service' : 'New Service'}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={!canSave}>
            Save Service
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        {/* Section: General */}
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          General
        </span>

        {/* Service Name */}
        <div>
          <label style={labelStyle}>
            Service Name<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Consultation"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Category */}
        <div>
          <label style={labelStyle}>Category</label>
          <select
            value={form.categoryId}
            onChange={(e) => update('categoryId', e.target.value)}
            style={{
              ...inputBaseStyle,
              cursor: 'pointer',
              appearance: 'auto',
            }}
          >
            <option value="">No category</option>
            {activeCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Description / Remarks */}
        <div>
          <label style={labelStyle}>Description / Remarks</label>
          <textarea
            placeholder="Optional details about this service"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            style={textareaStyle}
          />
        </div>

        {/* Typical Duration */}
        <div>
          <label style={labelStyle}>
            Typical Duration (mins)<span style={{ color: 'var(--color-danger)' }}> *</span>
          </label>
          <input
            type="number"
            placeholder="e.g. 30"
            min={0}
            value={form.duration}
            onChange={(e) => update('duration', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Price */}
        <div>
          <label style={labelStyle}>Price</label>
          <input
            type="number"
            placeholder="e.g. 1500"
            min={0}
            step="0.01"
            value={form.price}
            onChange={(e) => update('price', e.target.value)}
            style={inputBaseStyle}
          />
        </div>

        {/* Active Toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label style={{ ...labelStyle, marginBottom: 0 }}>Active</label>
          <SwitchToggle
            checked={form.active}
            onChange={(val) => update('active', val)}
            ariaLabel="Active"
          />
        </div>

        {/* PRD.305 — Laboratory Test. The persisted value is the canonical
            classification: the Laboratory module resolves its catalog from this
            flag alone and never from the category. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--sp-4)',
          }}
        >
          <span style={{ minWidth: 0 }}>
            <label style={{ ...labelStyle, marginBottom: 2 }}>Laboratory Test</label>
            <span
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              Makes this service orderable from Laboratory, Patient Labs and Consultation Labs.
            </span>
          </span>
          <SwitchToggle
            checked={form.isLabTest}
            onChange={(val) => {
              labTestTouchedRef.current = true;
              update('isLabTest', val);
            }}
            ariaLabel="Laboratory Test"
          />
        </div>

        {/* Coming Soon Section */}
        <div
          style={{
            borderTop: '1px solid var(--border)',
            paddingTop: 'var(--sp-4)',
            marginTop: 'var(--sp-2)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
            }}
          >
            Coming Soon
          </span>
          <div
            style={{
              marginTop: 'var(--sp-3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            <ComingSoonRow label="Service Image upload" />
            <ComingSoonRow label="Check-in QR enable" />
            <ComingSoonRow label="Notifications" />
          </div>
        </div>
      </div>
    </Drawer>
  );
}
